'use client';

import { useMemo, useState } from 'react';
import { IntentPrefetchLink } from '@/components/ui/intent-prefetch-link';
import { ArtDirectedImagePreload, ShopifyImage } from '@/components/ui/shopify-image';

interface Trade {
	name: string;
	slug: string;
	description?: string;
	image: { url: string; alt?: string } | null;
	url: string;
}

interface TradesGridProps {
	trades: Trade[];
	heading?: string;
	subheading?: string;
	heroImageUrl?: string;
	overlay?: string;
	showSearch?: boolean;
	showAlphabetNav?: boolean;
	columns?: string;
	variant?: 'page' | 'section';
}

const overlayClasses: Record<string, string> = {
	light: 'bg-gradient-to-t from-black/45 via-black/20 to-black/10',
	medium: 'bg-gradient-to-t from-black/65 via-black/35 to-black/20',
	dark: 'bg-gradient-to-t from-black/80 via-black/50 to-black/35',
	'extra-dark': 'bg-gradient-to-t from-black/90 via-black/65 to-black/50',
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: trade directory supports multiple layouts, grouping modes, and search states
export function TradesGrid({
	trades,
	heading,
	subheading,
	heroImageUrl,
	overlay = 'medium',
	showSearch = true,
	showAlphabetNav = false,
	columns = '4',
	variant = 'section',
}: TradesGridProps) {
	const [query, setQuery] = useState('');

	const filtered = useMemo(() => {
		if (!query.trim()) return trades;

		const normalizedQuery = query.toLowerCase();

		return trades.filter(
			(trade) =>
				trade.name.toLowerCase().includes(normalizedQuery) ||
				trade.description?.toLowerCase().includes(normalizedQuery),
		);
	}, [trades, query]);

	const grouped = useMemo(() => {
		const groups = new Map<string, Trade[]>();

		for (const trade of filtered) {
			const letter = trade.name.charAt(0).toUpperCase();

			if (!groups.has(letter)) groups.set(letter, []);
			groups.get(letter)?.push(trade);
		}

		return Array.from(groups.entries()).sort(([left], [right]) => left.localeCompare(right));
	}, [filtered]);

	const letters = useMemo(() => grouped.map(([letter]) => letter), [grouped]);
	// Without hero artwork, the first visible row supplies the page's LCP image.
	const priorityTrades =
		variant === 'page' && !heroImageUrl ? (grouped[0]?.[1].slice(0, 2) ?? []) : [];
	// Desktop can show more than the two mobile priority tiles. Scope these
	// hints to that viewport so its remaining LCP candidates are found in the
	// initial head without raising their download priority on phones.
	const desktopPriorityTrades =
		variant === 'page' && !heroImageUrl
			? (grouped[0]?.[1].slice(2, columns === '2' ? 2 : columns === '3' ? 3 : 4) ?? [])
			: [];

	const colClasses: Record<string, string> = {
		'2': 'grid-cols-1 sm:grid-cols-2',
		'3': 'grid-cols-2 lg:grid-cols-3',
		'4': 'grid-cols-2 lg:grid-cols-4',
	};

	const searchControl = showSearch ? (
		<form
			onSubmit={(event) => event.preventDefault()}
			className={
				variant === 'page'
					? 'mx-auto mt-10 flex w-full max-w-2xl overflow-hidden rounded-md border border-white/20 bg-neutral-900/70 backdrop-blur-sm'
					: 'mx-auto mb-12 flex w-full max-w-4xl overflow-hidden border border-neutral-300 bg-white'
			}
		>
			<input
				type="text"
				value={query}
				onChange={(event) => setQuery(event.target.value)}
				placeholder="What do you do for a living?"
				className={
					variant === 'page'
						? 'min-w-0 flex-1 bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-white/50 focus:outline-none md:px-5'
						: 'min-w-0 flex-1 bg-transparent px-5 py-3.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none'
				}
			/>
			<button
				type="submit"
				className={
					variant === 'page'
						? 'bg-neutral-950 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:bg-neutral-800 md:px-8'
						: 'bg-brand-primary px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-neutral-800'
				}
			>
				Search
			</button>
		</form>
	) : null;

	return (
		<>
			<ArtDirectedImagePreload
				variants={desktopPriorityTrades.flatMap((trade) =>
					trade.image
						? [
								{
									src: trade.image.url,
									sizes: '(min-width: 1024px) 25vw, 50vw',
									media: '(min-width: 1024px)',
								},
							]
						: [],
				)}
			/>
			{variant === 'page' && (
				<section className="relative flex min-h-[50vh] flex-col justify-center overflow-hidden bg-neutral-950 text-white md:min-h-[55vh]">
					{heroImageUrl && (
						<div className="absolute inset-0">
							<ShopifyImage
								src={heroImageUrl}
								alt=""
								fill
								loading="eager"
								fetchPriority="high"
								decoding="sync"
								sizes="100vw"
								className="object-cover grayscale"
							/>
							<div
								className={`absolute inset-0 ${overlayClasses[overlay] || overlayClasses.medium}`}
							/>
						</div>
					)}
					<div className="container-page relative z-10 pb-16 pt-28 text-center md:pb-20 md:pt-36">
						{heading && (
							<h1 className="text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl">
								{heading}
							</h1>
						)}
						{searchControl}
					</div>
				</section>
			)}

			<section className={variant === 'page' ? 'bg-white py-14 md:py-18' : ''}>
				<div className={variant === 'page' ? 'container-page' : ''}>
					{variant === 'section' && (heading || subheading) && (
						<div className="mb-10 text-center">
							{heading && (
								<h2 className="text-3xl font-black uppercase tracking-[0.12em] text-neutral-950 sm:text-4xl">
									{heading}
								</h2>
							)}
							{subheading && <p className="mt-3 text-neutral-600">{subheading}</p>}
						</div>
					)}

					{variant === 'section' && searchControl}

					{showAlphabetNav && letters.length > 1 && (
						<div className="mb-8 flex flex-wrap gap-2 border-b border-neutral-200 pb-6">
							{letters.map((letter) => (
								<a
									key={letter}
									href={`#trade-letter-${letter}`}
									className="text-xs font-black uppercase tracking-[0.18em] text-neutral-500 transition-colors hover:text-neutral-950"
								>
									{letter}
								</a>
							))}
						</div>
					)}

					{grouped.length === 0 ? (
						<div className="py-16 text-center">
							<p className="text-lg text-neutral-500">
								No trades found matching &ldquo;{query}&rdquo;
							</p>
							<button
								type="button"
								onClick={() => setQuery('')}
								className="mt-4 text-sm font-medium text-brand-accent hover:underline"
							>
								Clear search
							</button>
						</div>
					) : (
						<div className="space-y-16">
							{grouped.map(([letter, items]) => (
								<div key={letter} id={`trade-letter-${letter}`}>
									<h2 className="mb-8 text-4xl font-black uppercase tracking-[0.08em] text-neutral-950 sm:text-5xl">
										{letter}
									</h2>
									<div className={`grid ${colClasses[columns] || colClasses['4']} gap-6 lg:gap-8`}>
										{items.map((trade) => (
											<IntentPrefetchLink
												key={trade.slug}
												href={trade.url}
												className="group flex h-full flex-col"
											>
												<div className="relative aspect-[3/4] overflow-hidden bg-neutral-200">
													{trade.image ? (
														<ShopifyImage
															src={trade.image.url}
															alt={trade.image.alt || trade.name}
															fill
															loading={priorityTrades.includes(trade) ? 'eager' : undefined}
															fetchPriority={priorityTrades.includes(trade) ? 'high' : undefined}
															className="object-cover grayscale transition duration-500 group-hover:scale-[1.03]"
															sizes="(min-width: 1024px) 25vw, 50vw"
														/>
													) : (
														<div className="h-full w-full bg-neutral-200" />
													)}
												</div>
												<div className="mt-auto flex items-center justify-between gap-4 border-b border-neutral-200 py-4">
													<h3 className="text-[13px] font-black uppercase tracking-[0.18em] text-neutral-950 sm:text-[15px]">
														{trade.name}
													</h3>
													<svg
														className="h-4 w-4 shrink-0 text-neutral-950 transition-transform group-hover:translate-x-1"
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
														strokeWidth={2}
														aria-hidden="true"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M17 8l4 4m0 0l-4 4m4-4H3"
														/>
													</svg>
												</div>
											</IntentPrefetchLink>
										))}
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</section>
		</>
	);
}
